import { lazy, Suspense, useState } from "react";
import { LogOut } from "lucide-react";
import { LetterAvatar } from "@/components/LetterAvatar";
import { useAuth } from "@/hooks/useAuth";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import saleFishOnDark from "@/assets/logos/salefish-wordmark-on-dark.png";

/* The legal texts are long and read rarely: their own chunks, loaded when
   one is opened, not in every visitor's first download. */
const PrivacyPolicyDialog = lazy(() => import("@/components/PrivacyPolicyDialog").then((m) => ({ default: m.PrivacyPolicyDialog })));
const TermsOfUseDialog = lazy(() => import("@/components/TermsOfUseDialog").then((m) => ({ default: m.TermsOfUseDialog })));

/** Opens the account panel wherever it is mounted (AppShell listens). */
export const OPEN_ACCOUNT_EVENT = "salefish:open-account";
export const openAccountPanel = () => window.dispatchEvent(new Event(OPEN_ACCOUNT_EVENT));

/**
 * The foot of the menu — PropPay's: the wordmark, a rule, and who is signed in
 * (opening the account panel) beside the way out (Andrew, 2026-09-27).
 *
 * The avatar used to sit in the header's top-right corner. PropPay keeps the
 * person at the bottom-left of the menu, which is where the sidebar ends on a
 * desk and where the phone's menu sheet ends too — this same component closes
 * both, so the two cannot spell someone's role differently.
 */
export function SidebarFooter({ onNavigate, className }: { onNavigate?: () => void; className?: string }) {
  const { user, logout } = useAuth();
  const t = useTranslation();
  const [legal, setLegal] = useState<null | "privacy" | "terms">(null);
  if (!user) return null;

  const name = [user.firstName, user.lastName].filter(Boolean).join(" ") || user.email || t("shellNavAccountFallback", "Account");
  const role = user.role === "SUPER_ADMIN" ? t("salefishAdmin", "SaleFish Admin") : (user as any).userGroupDescription || "";

  return (
    <div className={cn("flex shrink-0 flex-col pt-4 menu-short:pt-2", className)}>
      <img src={saleFishOnDark} alt="SaleFish" decoding="async" className="menu-wordmark mb-4 ml-3 h-[18px] w-auto self-start menu-short:mb-2" />
      <div className="border-t border-sidebar-border" />
      <div className="mt-3 flex items-center gap-1.5 menu-short:mt-2">
        <button
          type="button"
          onClick={() => {
            onNavigate?.();
            openAccountPanel();
          }}
          aria-label={`${[name, role].filter(Boolean).join(", ")} — ${t("myAccount", "My Account") || "Account"}`}
          title={t("myAccount", "My Account") || "Account"}
          data-testid="button-user-avatar"
          className="flex min-w-0 flex-1 items-center gap-2.5 rounded p-1.5 text-left transition-colors duration-instant ease-standard hover:bg-[hsl(var(--menu-wash)/0.06)] outline-hidden focus-visible:ring-2 focus-visible:ring-sidebar-on-active"
        >
          <LetterAvatar name={name} size={36} flat />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-label font-semibold text-sidebar-strong">{name}</span>
            {role && <span className="block truncate text-micro text-sidebar-dim">{role}</span>}
          </span>
        </button>
        <button
          type="button"
          onClick={() => logout()}
          aria-label={t("signOut", "Sign Out") || "Sign out"}
          title={t("signOut", "Sign Out") || "Sign out"}
          data-testid="button-sidebar-logout"
          className="flex size-9 shrink-0 items-center justify-center rounded-full text-sidebar-muted transition-colors duration-instant ease-standard hover:bg-[hsl(var(--menu-wash)/0.08)] hover:text-sidebar-strong coarse:size-11 outline-hidden focus-visible:ring-2 focus-visible:ring-sidebar-on-active"
        >
          <LogOut className="size-[18px]" />
        </button>
      </div>
      {/* Under the account, where people look for them — PropPay's place for
          them, and in the phone's menu sheet too, since that draws this same
          foot. Quiet by colour alone (the panel's dim tier clears 4.5:1). */}
      <p className="ml-3 mt-2 flex flex-wrap items-center gap-x-1.5 text-micro text-sidebar-dim">
        <button type="button" onClick={() => setLegal("privacy")} className="rounded-xs underline-offset-2 transition-colors hover:text-sidebar-strong hover:underline focus-visible:underline focus-visible:outline-hidden" data-testid="button-privacy-policy">
          {t("shellTitlePrivacyPolicy", "Privacy Policy")}
        </button>
        <span aria-hidden="true" className="opacity-60">·</span>
        <button type="button" onClick={() => setLegal("terms")} className="rounded-xs underline-offset-2 transition-colors hover:text-sidebar-strong hover:underline focus-visible:underline focus-visible:outline-hidden" data-testid="button-terms-of-use">
          {t("shellLegalTermsTitle", "Terms of Service")}
        </button>
      </p>
      <Suspense fallback={null}>
        {legal === "privacy" && <PrivacyPolicyDialog defaultOpen onOpenChange={(o) => { if (!o) setLegal(null); }} />}
        {legal === "terms" && <TermsOfUseDialog defaultOpen onOpenChange={(o) => { if (!o) setLegal(null); }} />}
      </Suspense>
    </div>
  );
}
